import {
    IBaseConfig,
    IBaseGameConfig,
    IGameLoader,
    IMapQueryConfig,
} from "../types/IGameLoader.types";
import { IConfigs } from "../types/IConfigs.types";
import { IBaseConfigsLoaderTypes } from "../types/IBaseConfigsLoader.types";
import { IPartnerConfigsLoader } from "../types/IPartnerConfigsLoader.types";
import { IGlobalContext } from "../types/IGlobalContext.types";
import { Configs } from "../Configs";
import { BaseConfigsLoader } from "./BaseConfigsLoader";
import { PartnerConfigsLoader } from "./PartnerConfigsLoader";
import { isValidAbsoluteUrl, mergeObject } from "../utils";
import { getRevision } from "../../utils/revision.utils";
import {
    executeFunctionMapQueryParams,
    executeMapQueryParams,
} from "../../utils/parseMapQueryParams";
import { structuredClonePolyfill } from "../../utils/structuredClonePolyfill";
import { TypeInitParams } from "../types/TypeInitParams";

const DEFAULT_LANGUAGE_KEY = "en";

export class GameLoader implements IGameLoader {
    readonly configs: IConfigs;
    baseConfigsLoader?: IBaseConfigsLoaderTypes;
    partnerConfigsLoader?: IPartnerConfigsLoader;
    public userId: string | undefined;
    public sessionId: string | undefined;
    public activeGamePopup: boolean = false;
    public activePromoWidget: boolean = false;
    public isShellFeatureVisible: boolean = false;

    partnerConfigDocument: Document | null = null;

    global: IGlobalContext = {
        configs: {},
    };

    constructor(public initParams: TypeInitParams) {
        this.configs = new Configs();
    }

    async init(): Promise<void> {
        const revision = await getRevision();
        const { platform, partner } = this.initParams;

        this.baseConfigsLoader = new BaseConfigsLoader(this.configs, platform);
        await this.baseConfigsLoader.load(revision);

        const altPartner = this.findAlternativePartner();
        this.partnerConfigsLoader = new PartnerConfigsLoader(
            this.configs,
            partner || altPartner,
            altPartner
        );
        await this.partnerConfigsLoader.load(revision);

        window.addEventListener("send-config", () => this.sendConfigAsEvent());
    }

    setUserId(userId: string): void {
        this.userId = userId;
        this.sendConfigAsEvent();
    }
    setSessionId(sessionId: string): void {
        this.sessionId = sessionId;
        this.sendConfigAsEvent();
    }

    setActiveGamePopup(active: boolean): void {
        this.activeGamePopup = active;
        this.sendConfigAsEvent();
    }

    setActivePromoWidget(active: boolean): void {
        this.activePromoWidget = active;
    }

    setShellFeatureVisible(visible: boolean): void {
        this.isShellFeatureVisible = visible;
        this.sendConfigAsEvent();
    }

    generateUrl(gameName: string): string {
        if (!this.baseConfigsLoader) {
            throw new Error(`Base config need to init before!`);
        }

        if (!this.partnerConfigsLoader) {
            throw new Error(`Partner config need to init before!`);
        }

        const base = this.baseConfigsLoader.prepare(gameName);
        if (base === undefined) {
            throw new Error(`No base config found for game "${gameName}"`);
        }

        const partner = this.partnerConfigsLoader.prepare(gameName);
        if (partner === undefined) {
            throw new Error(`No partner config found for game "${gameName}"`);
        }

        const partnerConfig = base.filter.reduce(
            (object: Partial<IBaseConfig>, key: string) =>
                mergeObject(object, partner.filters.get(key) || {}),
            {}
        );

        const commonConfig = mergeObject(base.config, partnerConfig);
        const commonMapQuery = mergeObject(base.mapQuery, partner.mapQuery);
        return this.buildURL(commonConfig, commonMapQuery, gameName);
    }

    buildURL(
        config: Partial<IBaseGameConfig>,
        mapQuery: Partial<IMapQueryConfig>,
        gameName: string
    ): string {
        if (!this.baseConfigsLoader) {
            throw new Error(`Base config need to init before!`);
        }

        const priorityUrlParams = Array.from(
            this.initParams.params.entries()
        ).reduce(
            (object, [key, value]) => ({
                ...object,
                [key]: value,
            }),
            {}
        );

        const prepareConfig: Partial<IBaseGameConfig> = {
            ...config,
            ...priorityUrlParams,
            // more priority (override)
            ...{
                gameName: config.gameName,
            },
        };

        const localUrl = this.prepareGameEndPointUrl(prepareConfig);

        const preMapQueryParams = executeMapQueryParams(
            prepareConfig,
            mapQuery,
            // if <mapQuery> not found > using configs as old GM
            Object.keys(mapQuery).length > 0 ? {} : prepareConfig
        );
        const finishMapQueryParams = executeFunctionMapQueryParams(
            preMapQueryParams,
            mapQuery,
            prepareConfig
        );

        if (
            finishMapQueryParams.lang &&
            this.baseConfigsLoader.langs.size > 0
        ) {
            if (!this.baseConfigsLoader.langs.has(finishMapQueryParams.lang)) {
                console.warn(
                    `Language "${finishMapQueryParams.lang}" is not supported! Switching to EN language..`
                );

                if (!this.baseConfigsLoader.langs.has(DEFAULT_LANGUAGE_KEY)) {
                    throw new Error(
                        `Default language "${DEFAULT_LANGUAGE_KEY}" is not supported!`
                    );
                }

                finishMapQueryParams.lang = DEFAULT_LANGUAGE_KEY;
            }
        }

        const readyParams: Array<{ key: string; value: string }> = Object.keys(
            finishMapQueryParams
        ).reduce(
            (list: Array<{ key: string; value: string }>, key) => [
                ...list,
                { key, value: (finishMapQueryParams as any)[key] },
            ],
            []
        );

        if (readyParams.length > 0) {
            readyParams.forEach(({ value, key }) => {
                localUrl.searchParams.set(key, value);
            });
        } else {
            console?.warn(`[GR] not configurated "config > mapQuery > query"`);

            this.initParams.params.forEach((value, key) => {
                localUrl.searchParams.set(key, value);
            });
        }

        this.writeConfigsToGlobal(
            localUrl.searchParams,
            prepareConfig,
            gameName
        );

        return localUrl.href;
    }

    protected prepareGameEndPointUrl(
        prepareConfig: Partial<IBaseGameConfig>
    ): URL {
        if (!prepareConfig.gameName) {
            throw new Error(`Parameter "gameName" not found!`);
        }

        if (!prepareConfig.baseUrl) {
            throw new Error(
                `Parameter "baseUrl" for game "${prepareConfig.gameName}" not found!`
            );
        }

        const launchGameUrl = prepareConfig.baseUrl.replace(
            "{GAME_NAME}",
            prepareConfig.gameName
        );

        const domain =
            this.configs.getGameLaunchUrl() || window.location.origin;

        return new URL(
            isValidAbsoluteUrl(launchGameUrl)
                ? launchGameUrl
                : `${domain}${launchGameUrl}`
        );
    }

    protected checkRunnerMode(): void {
        if (
            this.global.configs?.key &&
            /^replay-/.test(this.global.configs.key)
        ) {
            this.global.configs.mode = "replay";
        }
    }

    protected writeConfigsToGlobal(
        searchParams: URLSearchParams,
        config: Partial<IBaseGameConfig>,
        gameName: string
    ) {
        this.global.configs = structuredClonePolyfill({
            ...config,
            ...Array.from(searchParams.entries()).reduce(
                (obj, [key, value]) => ({
                    ...obj,
                    [key]: value,
                }),
                {}
            ),
        });

        this.global.configs.gameName = gameName;
        this.checkRunnerMode();

        this.sendConfigAsEvent();
    }

    protected sendConfigAsEvent(): void {
        window.dispatchEvent(
            new CustomEvent("config-updated", {
                detail: {
                    ...(this.global || {}),
                    configs: {
                        ...(this.global.configs || {}),
                        userId: this.userId,
                        sessionId: this.sessionId,
                        activeGamePopup: this.activeGamePopup,
                        isShellFeatureVisible: this.isShellFeatureVisible,
                    },
                },
            })
        );
    }

    protected findAlternativePartner(): string | undefined {
        if (!this.baseConfigsLoader) {
            return undefined;
        }

        const { key, partner } = this.initParams;

        this.baseConfigsLoader.prepareAltPartnersConfig();
        const altPartners = this.baseConfigsLoader.altPartners;
        const existConfig = altPartners && Object.keys(altPartners).length > 0;

        switch (true) {
            case key && /^replay-/.test(key):
                return (() => {
                    if (!partner && (!existConfig || !altPartners["replay"])) {
                        throw new Error(`Replay partner config not found!`);
                    }

                    return altPartners["replay"];
                })();

            case existConfig:
                return altPartners["default"];
        }

        return undefined;
    }
}
